/**
 * Segmented mode toggle, visible only for hybrid-role users.
 * Clicking the inactive segment (or Ctrl/⌘+M) switches mode and reloads the interface store.
 */
import * as React from 'react';
import { School, Monitor, Loader2 } from 'lucide-react';
import { useMode } from '@/contexts/ModeContext';
import { cn } from '@/lib/utils';
import { formatShortcutAria, formatShortcutLabel } from '@/utils/keyboard-shortcut-utils';

/** Key used with Ctrl/⌘ to toggle in-person / remote. */
const MODE_SWITCH_KEY = 'm';

/** True when the event target is a field — the shortcut must not steal typing. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Navbar control that toggles in-person vs remote mode. */
export const ModeSwitchButton: React.FC = () => {
  const { mode, setMode, canSwitchMode, isSwitching } = useMode();

  React.useEffect(() => {
    if (!canSwitchMode) return;

    function handleKeyDown(event: KeyboardEvent): void {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== MODE_SWITCH_KEY) {
        return;
      }
      if (event.altKey || event.shiftKey) return;
      if (isSwitching || !mode || isEditableTarget(event.target)) return;
      event.preventDefault();
      setMode(mode === 'in_person' ? 'remote' : 'in_person');
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canSwitchMode, isSwitching, mode, setMode]);

  if (!canSwitchMode) return null;

  const shortcutLabel = formatShortcutLabel(MODE_SWITCH_KEY);
  const shortcutAria = formatShortcutAria(MODE_SWITCH_KEY);

  function handleInPersonClick(): void {
    setMode('in_person');
  }

  function handleRemoteClick(): void {
    setMode('remote');
  }

  return (
    <div className="mode-switch-row">
      <div
        className="mode-switch"
        aria-label="Basculer entre présentiel et distanciel"
        aria-keyshortcuts={shortcutAria}
      >
        <button
          type="button"
          className={cn(
            'mode-switch__option',
            mode === 'in_person' && 'mode-switch__option--active',
          )}
          aria-pressed={mode === 'in_person'}
          disabled={isSwitching}
          title="Mode présentiel"
          onClick={handleInPersonClick}
        >
          {isSwitching && mode !== 'in_person' ? (
            <Loader2 className="mode-switch__icon mode-switch__icon--spin" aria-hidden />
          ) : (
            <School className="mode-switch__icon" aria-hidden />
          )}
          <span className="mode-switch__label">Présentiel</span>
        </button>

        <button
          type="button"
          className={cn('mode-switch__option', mode === 'remote' && 'mode-switch__option--active')}
          aria-pressed={mode === 'remote'}
          disabled={isSwitching}
          title="Mode distanciel"
          onClick={handleRemoteClick}
        >
          {isSwitching && mode !== 'remote' ? (
            <Loader2 className="mode-switch__icon mode-switch__icon--spin" aria-hidden />
          ) : (
            <Monitor className="mode-switch__icon" aria-hidden />
          )}
          <span className="mode-switch__label">Distanciel</span>
        </button>
      </div>
      <kbd className="mode-switch__shortcut">{shortcutLabel}</kbd>
    </div>
  );
};
